import { Injectable, NgZone } from '@angular/core';

// external libs
import { Observable, Observer } from 'rxjs';
import * as Overlay from '@h21-map/google-overlay';

// services
import { MarkerClusterService } from '../abstract/abstract-marker-cluster';

// directives
import { H21MapMarkerClusterDirective } from '../../directives/h21-map-marker-cluster.directive';

// models
import { FeatureEvent } from '../../models/feature-event.model';
import { Position } from '../../models/position.model';
import { CircleEvent } from '../../models/circle-event.model';
import { MarkerClusterFilter } from '../../models/marker-cluster-filter.model';

@Injectable()
export class GoogleMarkerClusterService extends MarkerClusterService<google.maps.Map, google.maps.Marker, google.maps.Polyline> {

    private _markerCluster: Map<H21MapMarkerClusterDirective, google.maps.Data> = new Map<H21MapMarkerClusterDirective, google.maps.Data>();
    private _layer: H21MapMarkerClusterDirective;

    constructor(private _zone: NgZone) {
        super();
    }

    public addMarkerCluster(markerCluster?: H21MapMarkerClusterDirective): void {
        this._layer = markerCluster;
        this._createDataLayer().subscribe((markerClusterData) => {
            this._markerCluster.set(markerCluster, markerClusterData);
            markerClusterData.setMap(this.map.api);
            this.createOverlay(markerCluster);
        });
    }

    public createOverlay(markerCluster?: H21MapMarkerClusterDirective): void {
        this._markerCluster.get(markerCluster) && this._markerCluster.get(markerCluster).set('overlay', []);
        if (!markerCluster.geoJson) { return; }
        this._getArray(markerCluster).subscribe((() => {
            this._addMarkerCluster(markerCluster);
        }));
    }

    public updateMarkerCluster(markerCluster: H21MapMarkerClusterDirective): void {
        this.getCountLoadMarkers(markerCluster);
        this._removeOverlay(markerCluster).subscribe(() => {
            this._removeFeature(markerCluster);
            this.createOverlay(markerCluster);
            this.addListener.next(true);
            this.ready$.next();
        });
    }

    public removeMarkerCluster(markerCluster?: H21MapMarkerClusterDirective): void {
        this._removeOverlay(markerCluster).subscribe(() => {
            if (this._markerCluster.get(markerCluster)) {
                this._markerCluster.get(markerCluster).setMap(null);
                this._markerCluster.delete(markerCluster);
            }
        });
    }

    public removeMarkerClusters(): void {
        this._markerCluster.forEach((key) => {
            key && key.setMap(null);
        });
        this._markerCluster.clear();
    }

    public removeFeature(markerCluster: H21MapMarkerClusterDirective): void {
        this._removeOverlay(markerCluster).subscribe(() => {
            this._removeFeature(markerCluster);
        });
    }

    public createEvent<E>(eventName: string, markerCluster: H21MapMarkerClusterDirective, isOverlay?: boolean): Observable<E> {
        return new Observable((observer: Observer<E>) => {
            this._markerCluster.get(markerCluster) && this._markerCluster.get(markerCluster).addListener(eventName, (event) => {
                this._zone.run(() => observer.next(event));
            });
            this._markerCluster.get(markerCluster) && this._markerCluster.get(markerCluster).get('overlay').forEach((value) => {
                value.addListener(eventName, (event: E) => this._zone.run(() => observer.next(event)));
            });
        });
    }
    public createEventMouseOver<D>(markerCluster: H21MapMarkerClusterDirective): Observable<D> {
        return new Observable((observer: Observer<any>) => {
            this._markerCluster.get(markerCluster) && google.maps.event.clearListeners(this._markerCluster.get(markerCluster), 'mouseout');
            this.createEvent('mouseover', markerCluster).subscribe((event: any) => {
                this._zone.run(() => observer.next(this._createEventField(event)));
                this._markerCluster.get(markerCluster).revertStyle();
                this._overrideStyle(markerCluster, event);
                if (event.feature.type === 'Cluster') { return; }
            });
        });
    }

    public createEventMouseOut<D>(markerCluster: H21MapMarkerClusterDirective): Observable<D> {
        return new Observable((observer: Observer<any>) => {
            this._markerCluster.get(markerCluster) && google.maps.event.clearListeners(this._markerCluster.get(markerCluster), 'mouseout');
            this.createEvent('mouseout', markerCluster).subscribe((event: any) => {
                this._markerCluster.get(markerCluster).revertStyle();
                this._zone.run(() => observer.next(this._createEventField(event)));
                if (event.feature.type === 'Cluster') { return; }
                if (event.e) {
                    event.e.target.style.zIndex = event.feature.properties.zIndex || 100;
                }
            });
        });
    }

    public createEventMouseClick<DE>(markerCluster: H21MapMarkerClusterDirective): Observable<DE> {
        return new Observable((observer: Observer<any>) => {
            this._markerCluster.get(markerCluster) && google.maps.event.clearListeners(this._markerCluster.get(markerCluster), 'click');
            this.createEvent('click', markerCluster).subscribe((event: any) => {
                observer.next(this._createEventField(event));
                if (event.feature.type && event.feature.type === 'Cluster') {
                    this._clusterFitbounds(event);
                }
            });
        });
    }

    public setModeCircle(CircleOptions: CircleEvent): void {
        this.modeCircle.next(CircleOptions);
    }

    public setFilter(filter: MarkerClusterFilter): void {
        this.changeFilter.next(filter);
    }

    public clearMarkerCluster(): void {
        this.removeFeature(this._layer);
        this.clearMarkerCluster$.next();
    }

    public getBounds(): Observable<any> {
        try {
            return new Observable((observer: Observer<any>) => {
                const bounds = new google.maps.LatLngBounds();
                const arrayOverlay = this._markerCluster.get(this._layer)
                    && this._markerCluster.get(this._layer).get('overlay');
                arrayOverlay &&
                    arrayOverlay.forEach((value) => {
                        bounds.extend(value.getPosition());
                    });
                observer.next(bounds);
            });
        } catch { }
    }

    public updateStyle(id: number, position?: Position): void {
        this._redrawStyle();
        try {
            const feature = this._markerCluster.get(this._layer).get('overlay').find((val) => val.feature.id === id);
            feature ? this._overlayStyle(feature) : this.isMarkerCluster.next(position);
        } catch { }
    }

    private _createDataLayer(options?: google.maps.Data.DataOptions): Observable<google.maps.Data> {
        return new Observable((observer: Observer<google.maps.Data>) => {
            observer.next(new google.maps.Data(options));
        });
    }

    private _getArray(markerCluster?: H21MapMarkerClusterDirective): Observable<any> {
        return new Observable((observer) => {
            if (!markerCluster.geoJson.features && !markerCluster) { return; }
            markerCluster.geoJson.features && markerCluster.geoJson.features.forEach((feature) => {
                if (!feature.properties.iconUrl) {
                    const overlay = this._createOverlay(feature);
                    this._markerCluster.get(markerCluster) && this._markerCluster.get(markerCluster).get('overlay').push(overlay);
                }
            });
            markerCluster.geoJson.features = markerCluster.geoJson.features.filter((item: any) => item.properties.iconUrl !== undefined);
            observer.next();
            observer.complete();
        });

    }

    private _overrideStyle(markerCluster: H21MapMarkerClusterDirective, event: any) {
        if (event.e) {
            event.e.target.style.zIndex = event.feature.properties.zIndexSelected;
        } else {
            const icon = event.feature.getProperty('iconUrlSelected');
            const zIndex = event.feature.getProperty('zIndexSelected');
            this._markerCluster.get(markerCluster).overrideStyle(event.feature, {
                icon,
                zIndex,
            });
        }
    }

    private _createOverlay(feature?: any): any {
        try {
            if (google.maps) {
                const _overlay = Overlay.default(google.maps);
                return new _overlay(this._createOptions(feature));
            }

        } catch { }
    }

    private _overlayStyle(value): void {
        if (value.feature.type === 'Cluster') {
            value.iconClass = `${value.feature.properties.iconClass}${' '}${value.feature.properties.iconClassSelected}`;
            value.label.setStyles();
            return;
        }
        value.iconClass = 'h21-map-price-marker active';
        value.label.setStyles();
    }

    private _createOptions(feature?: any): any {
        return {
            opacity: 0,
            iconContent: feature.properties.iconContent,
            visible: false,
            feature: feature,
            shape: {},
            zIndex: feature.properties.zIndex,
            map: this.map.api,
            iconVisible: true,
            iconAnchor: new google.maps.Point(0, 30),
            iconClass: feature.properties.iconClass,
            position: new google.maps.LatLng(feature.geometry.coordinates[1], feature.geometry.coordinates[0]),
        };
    }

    private _addMarkerCluster(markerCluster: H21MapMarkerClusterDirective): void {
        this._markerCluster.get(markerCluster) && this._markerCluster.get(markerCluster).addGeoJson(markerCluster.geoJson);
        this._markerCluster.get(markerCluster) && this._addDataStyles(markerCluster);
    }

    private _addDataStyles(markerCluster?: H21MapMarkerClusterDirective): void {
        this._markerCluster.get(markerCluster).setStyle((feature) => {
            return ({
                icon: {
                    url: feature.getProperty('iconUrl'),
                    zIndex: feature.getProperty('zIndex'),
                },
            });
        });
    }

    private _removeFeature(markerCluster: H21MapMarkerClusterDirective): void {
        try {
            const _layer = this._markerCluster.get(markerCluster);
            _layer && _layer.forEach((feature) => {
                _layer.remove(feature);
            });
        } catch { }
    }

    private _removeOverlay(markerCluster: H21MapMarkerClusterDirective): Observable<any> {
        try {
            return new Observable((observer) => {
                const _layer = this._markerCluster.get(markerCluster);
                _layer && _layer.get('overlay') && _layer.get('overlay').forEach((overlay) => {
                    overlay && overlay.setMap(null);
                });
                _layer && _layer.set('overlay', null);
                observer.next();
                observer.complete();
            });
        } catch { }
    }

    private _createEventField(event: any): FeatureEvent {
        const pixel = this.map.conversions.translateLatLngClientXY(event);
        return new FeatureEvent(
            new Position(event.latLng.lat(), event.latLng.lng()),
            pixel.clientX,
            pixel.clientY + 5,
            event.feature.getProperty ? event.feature.getProperty('data') : event.feature.properties.data || null,
            event.feature.type ? event.feature.type : 'Feature',
        );
    }

    private _redrawStyle(): void {
        const _layer = this._markerCluster.get(this._layer);
        _layer && _layer.get('overlay') && _layer.get('overlay').forEach((overlay) => {
            if (overlay.feature.type === 'Cluster') {
                overlay.iconClass = `${overlay.feature.properties.iconClass}`;
                overlay.label.setStyles();
                return;
            } else {
                overlay.iconClass = 'h21-map-price-marker';
                overlay.label.setStyles();
            }
        });
    }

    private _clusterFitbounds(event: any): void {
        const bbox = event.feature.bbox;
        const bounds = new google.maps.LatLngBounds(
            new google.maps.LatLng(bbox[0][1], bbox[0][0]),
            new google.maps.LatLng(bbox[1][1], bbox[1][0]),
        );

        this.map.api.panToBounds(bounds);
        this.map.api.fitBounds(bounds, 10);
    }

}
